import { useState, useEffect, useMemo, useRef, useCallback, Component } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapTrifold, NavigationArrow, Crosshair, X, Minus, ArrowsOut, Compass } from "@phosphor-icons/react";
import { format } from "date-fns";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const US_CENTER = [39.8283, -98.5795];
const US_ZOOM = 4;

function isValidLatLng(lat, lng) {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    isFinite(lat) &&
    isFinite(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180
  );
}

const createCustomIcon = (category) => {
  const colors = { festival: "#E07A5F", fair: "#81B29A", community: "#2B2D42" };
  const color = colors[category] || colors.community;
  return L.divIcon({
    className: "custom-marker-container",
    html: `<div style="width:24px;height:24px;background:${color};border:2.5px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;"><div style="width:6px;height:6px;background:white;border-radius:50%;"></div></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const userLocationIcon = L.divIcon({
  className: "user-location-marker",
  html: `<div style="position:relative;width:36px;height:36px;"><div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:36px;height:36px;background:rgba(59,130,246,0.15);border-radius:50%;animation:pulse-ring 2s ease-out infinite;"></div><div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:14px;height:14px;background:#3B82F6;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(59,130,246,0.5);"></div></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18]
});

// Safe map view updater — uses setView with try/catch, never flyTo on mount
const MapViewController = ({ center, zoom, skipInitial }) => {
  const map = useMap();
  const isFirstRender = useRef(true);
  const prevCenter = useRef(null);

  useEffect(() => {
    if (!center || !isValidLatLng(center[0], center[1])) return;

    // Skip the very first render — MapContainer already sets initial view
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevCenter.current = center;
      return;
    }

    // Only move if center actually changed
    const same = prevCenter.current &&
      prevCenter.current[0] === center[0] &&
      prevCenter.current[1] === center[1];
    if (same) return;

    prevCenter.current = center;

    try {
      map.setView(center, zoom, { animate: true, duration: 0.5 });
    } catch (e) {
      // Fallback: no animation
      try { map.setView(center, zoom, { animate: false }); } catch (err) { console.error("Map setView failed:", err.message); }
    }
  }, [center, zoom, map]);

  return null;
};

// Invalidate map size when container resizes
const MapResizer = ({ expanded }) => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      try { map.invalidateSize(); } catch (_) {}
    }, 350);
    return () => clearTimeout(timer);
  }, [expanded, map]);
  return null;
};

// Error boundary so map errors never crash the whole app
class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.warn("Map error caught:", error?.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full bg-[#F7F5F0] rounded-xl p-8">
          <div className="text-center">
            <MapTrifold weight="duotone" className="w-10 h-10 text-[#6C757D] mx-auto mb-2" />
            <p className="text-sm text-[#6C757D] font-medium">Map unavailable</p>
            <button
              onClick={() => this.setState({ hasError: false })}
              className="mt-2 text-xs text-[#E07A5F] hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const MapView = ({ events, onMarkerClick, selectedEvent, userLocation, nearMeActive, nearMeRadius, isVisible, onClose, isMinimized, onToggleMinimize }) => {
  const mapRef = useRef(null);

  const mapCenter = useMemo(() => {
    if (nearMeActive && userLocation && isValidLatLng(userLocation.lat, userLocation.lng)) {
      return [userLocation.lat, userLocation.lng];
    }
    if (selectedEvent && isValidLatLng(selectedEvent.latitude, selectedEvent.longitude)) {
      return [selectedEvent.latitude, selectedEvent.longitude];
    }
    return US_CENTER;
  }, [selectedEvent, nearMeActive, userLocation]);

  const mapZoom = useMemo(() => {
    if (nearMeActive && userLocation) {
      if (nearMeRadius <= 25) return 9;
      if (nearMeRadius <= 50) return 8;
      if (nearMeRadius <= 100) return 7;
      if (nearMeRadius <= 200) return 6;
      if (nearMeRadius <= 350) return 5;
      return 4;
    }
    if (selectedEvent) return 8;
    return US_ZOOM;
  }, [selectedEvent, nearMeActive, userLocation, nearMeRadius]);

  const radiusInMeters = (nearMeRadius || 50) * 1609.34;

  const getCategoryLabel = (cat) => ({ festival: "Festival", fair: "Fair", community: "Community" }[cat] || cat);

  const handleResetView = useCallback(() => {
    try {
      if (mapRef.current) {
        mapRef.current.setView(US_CENTER, US_ZOOM, { animate: true, duration: 0.5 });
      }
    } catch (err) { console.error("Map reset failed:", err.message); }
  }, []);

  // Filter events to only those with valid coordinates
  const validEvents = useMemo(() => {
    return events.filter(e => isValidLatLng(e.latitude, e.longitude));
  }, [events]);

  const circleOptions = useMemo(() => (
    { color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.06, weight: 2, dashArray: "6 4" }
  ), []);

  if (!isVisible) return null;

  return (
    <div
      className="bg-white rounded-xl border border-[#D6D3D1] shadow-sm overflow-hidden transition-all duration-300"
      data-testid="map-view-container"
    >
      {/* Map Header */}
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-[#D6D3D1]/50 bg-[#F7F5F0]/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapTrifold weight="duotone" className="w-4 h-4 sm:w-5 sm:h-5 text-[#E07A5F]" />
          <h2 className="text-sm sm:text-base font-semibold text-[#2B2D42]" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {nearMeActive ? "Shows Near You" : "Show Locations"}
          </h2>
          <span className="text-[10px] sm:text-xs text-[#6C757D] ml-1">({validEvents.length})</span>
        </div>

        <div className="flex items-center gap-1">
          {!isMinimized && (
            <div className="hidden sm:flex items-center gap-3 mr-3 text-[10px]">
              {nearMeActive && (
                <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /><span className="text-[#6C757D]">You</span></div>
              )}
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-[#E07A5F]" /><span className="text-[#6C757D]">Festival</span></div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-[#81B29A]" /><span className="text-[#6C757D]">Fair</span></div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full bg-[#2B2D42]" /><span className="text-[#6C757D]">Community</span></div>
            </div>
          )}

          {!isMinimized && (
            <button onClick={handleResetView} className="p-1.5 rounded-md hover:bg-[#E07A5F]/10 text-[#6C757D] hover:text-[#E07A5F] transition-colors" title="Reset map view" data-testid="map-reset-btn">
              <Compass weight="duotone" className="w-4 h-4" />
            </button>
          )}

          <button onClick={onToggleMinimize} className="p-1.5 rounded-md hover:bg-[#E07A5F]/10 text-[#6C757D] hover:text-[#E07A5F] transition-colors" title={isMinimized ? "Expand map" : "Minimize map"} data-testid="map-minimize-btn">
            {isMinimized ? <ArrowsOut weight="bold" className="w-4 h-4" /> : <Minus weight="bold" className="w-4 h-4" />}
          </button>

          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-red-50 text-[#6C757D] hover:text-red-500 transition-colors" title="Close map" data-testid="map-close-btn">
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Map Body */}
      <div className={`relative transition-all duration-300 ease-in-out overflow-hidden ${isMinimized ? "h-0" : "h-[300px] sm:h-[400px] lg:h-[calc(100vh-340px)] min-h-[250px] sm:min-h-[350px]"}`}>
        <MapErrorBoundary>
          <MapContainer
            ref={mapRef}
            center={US_CENTER}
            zoom={US_ZOOM}
            className="h-full w-full"
            scrollWheelZoom={true}
            dragging={true}
            zoomControl={true}
            doubleClickZoom={true}
            touchZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <MapViewController center={mapCenter} zoom={mapZoom} />
            <MapResizer expanded={!isMinimized} />

            {/* User location + radius */}
            {nearMeActive && userLocation && isValidLatLng(userLocation.lat, userLocation.lng) && (
              <>
                <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
                  <Popup>
                    <div className="p-1 text-center">
                      <p className="font-semibold text-[#3B82F6] text-sm">Your Location</p>
                      <p className="text-xs text-[#6C757D] mt-1">Within {nearMeRadius} miles</p>
                    </div>
                  </Popup>
                </Marker>
                <Circle
                  center={[userLocation.lat, userLocation.lng]}
                  radius={radiusInMeters}
                  pathOptions={circleOptions}
                />
              </>
            )}

            {/* Event markers */}
            {validEvents.map((event) => (
              <Marker
                key={event.id}
                position={[event.latitude, event.longitude]}
                icon={createCustomIcon(event.category)}
                eventHandlers={{ click: () => onMarkerClick(event) }}
              >
                <Popup className="custom-popup">
                  <div className="p-1 min-w-[180px]">
                    <div className="flex items-start gap-2 mb-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        event.category === "festival" ? "badge-festival" : event.category === "fair" ? "badge-fair" : "badge-community"
                      }`}>
                        {getCategoryLabel(event.category)}
                      </span>
                      {event.distance_miles != null && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600">
                          <NavigationArrow weight="bold" className="w-2.5 h-2.5" />{event.distance_miles} mi
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-[#2B2D42] text-xs mb-0.5">{event.name}</h3>
                    <p className="text-[10px] text-[#6C757D] mb-1">{event.location}</p>
                    <p className="text-[10px] text-[#E07A5F] font-medium">
                      {format(new Date(event.start_date), "MMM d, yyyy")}
                      {event.time && <span className="text-[#6C757D]"> &bull; {event.time}</span>}
                    </p>
                    <button onClick={() => onMarkerClick(event)} className="mt-1.5 w-full text-[10px] font-medium text-[#E07A5F] hover:text-[#D46A50] flex items-center justify-center gap-1">
                      View Details <NavigationArrow weight="bold" className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </MapErrorBoundary>

        {/* Empty overlays */}
        {validEvents.length === 0 && !nearMeActive && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F7F5F0]/80 backdrop-blur-sm">
            <div className="text-center">
              <MapTrifold weight="duotone" className="w-10 h-10 text-[#6C757D] mx-auto mb-2" />
              <p className="text-sm text-[#6C757D]">No shows to display</p>
            </div>
          </div>
        )}
        {validEvents.length === 0 && nearMeActive && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#F7F5F0]/80 backdrop-blur-sm pointer-events-none">
            <div className="text-center">
              <Crosshair weight="duotone" className="w-10 h-10 text-[#6C757D] mx-auto mb-2" />
              <p className="text-sm text-[#6C757D] font-medium">No shows within {nearMeRadius} miles</p>
              <p className="text-xs text-[#6C757D] mt-1">Try a larger radius</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;

